/**
 * useTimelineZoom Hook
 *
 * Provides interactive pan/zoom behavior for timeline containers.
 * Handles wheel zoom (with center-point zoom) and drag pan.
 */

import { useEffect, useCallback, useRef, type RefObject } from 'react'
import type { TimelineTransform, ZoomConstraints } from '../types'

interface UseTimelineZoomOptions {
  /** Ref to the container element to attach zoom behavior to */
  containerRef: RefObject<HTMLElement>
  /** Current transform state */
  transform: TimelineTransform
  /** Callback to update transform state */
  onTransformChange: (transform: TimelineTransform) => void
  /** Zoom constraints (min/max scale) */
  constraints: ZoomConstraints
  /** Container width for calculating pan bounds */
  containerWidth: number
  /** Whether zoom/pan is enabled. Default: true */
  enabled?: boolean
}

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Constrain transform to valid bounds
 * Only constrains zoom (k), allows infinite panning (x)
 */
function constrainTransform(
  transform: TimelineTransform,
  constraints: ZoomConstraints,
  _containerWidth: number
): TimelineTransform {
  const k = clamp(transform.k, constraints.minScale, constraints.maxScale)
  return { x: transform.x, y: 0, k }
}

/**
 * Attaches interactive pan/zoom behavior to a timeline container.
 *
 * Features:
 * - Wheel zoom with zoom-toward-cursor behavior
 * - Drag pan with proper bounds
 * - Touch support for pinch zoom and drag
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null)
 * useTimelineZoom({
 *   containerRef,
 *   transform: state.transform,
 *   onTransformChange: setTransform,
 *   constraints: { minScale: 0.5, maxScale: 10, zoomStep: 1.5 },
 *   containerWidth: 800,
 * })
 */
export function useTimelineZoom({
  containerRef,
  transform,
  onTransformChange,
  constraints,
  containerWidth,
  enabled = true,
}: UseTimelineZoomOptions): void {
  // Track drag state
  const isDragging = useRef(false)
  const lastX = useRef(0)
  const lastTouchDistance = useRef<number | null>(null)

  // Zoom toward a point (cursor position)
  const zoomAtPoint = useCallback(
    (delta: number, centerX: number) => {
      const zoomFactor = delta < 0 ? constraints.zoomStep : 1 / constraints.zoomStep
      const newK = clamp(
        transform.k * zoomFactor,
        constraints.minScale,
        constraints.maxScale
      )

      // If scale didn't change (at limits), don't update
      if (newK === transform.k) return

      // Zoom toward the cursor position
      // The point under the cursor should stay under the cursor after zoom
      const scaleDiff = newK / transform.k
      const newX = centerX - (centerX - transform.x) * scaleDiff

      const constrained = constrainTransform(
        { x: newX, y: 0, k: newK },
        constraints,
        containerWidth
      )

      onTransformChange(constrained)
    },
    [transform, constraints, containerWidth, onTransformChange]
  )

  // Pan by delta
  const pan = useCallback(
    (deltaX: number) => {
      const newX = transform.x + deltaX
      const constrained = constrainTransform(
        { x: newX, y: 0, k: transform.k },
        constraints,
        containerWidth
      )

      if (constrained.x !== transform.x) {
        onTransformChange(constrained)
      }
    },
    [transform, constraints, containerWidth, onTransformChange]
  )

  // Handle wheel events
  useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = container.getBoundingClientRect()
      const centerX = e.clientX - rect.left
      zoomAtPoint(e.deltaY, centerX)
    }

    container.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      container.removeEventListener('wheel', handleWheel)
    }
  }, [enabled, containerRef, zoomAtPoint])

  // Handle mouse drag for pan
  useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current

    const handleMouseDown = (e: MouseEvent) => {
      // Only start drag on primary button
      if (e.button !== 0) return
      isDragging.current = true
      lastX.current = e.clientX
      container.style.cursor = 'grabbing'
      e.preventDefault()
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      const deltaX = e.clientX - lastX.current
      lastX.current = e.clientX
      pan(deltaX)
    }

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false
        container.style.cursor = 'grab'
      }
    }

    container.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    // Set initial cursor
    container.style.cursor = 'grab'

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      container.style.cursor = ''
    }
  }, [enabled, containerRef, pan])

  // Handle touch events for mobile
  useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current

    const getTouchDistance = (touches: TouchList): number => {
      if (touches.length < 2) return 0
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const getTouchCenter = (touches: TouchList): number => {
      if (touches.length < 2) return touches[0]?.clientX ?? 0
      return (touches[0].clientX + touches[1].clientX) / 2
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        // Single touch - start pan
        isDragging.current = true
        lastX.current = e.touches[0].clientX
      } else if (e.touches.length === 2) {
        // Two touches - start pinch zoom
        lastTouchDistance.current = getTouchDistance(e.touches)
        isDragging.current = false
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()

      if (e.touches.length === 1 && isDragging.current) {
        // Single touch pan
        const deltaX = e.touches[0].clientX - lastX.current
        lastX.current = e.touches[0].clientX
        pan(deltaX)
      } else if (e.touches.length === 2 && lastTouchDistance.current !== null) {
        // Pinch zoom
        const newDistance = getTouchDistance(e.touches)
        const rect = container.getBoundingClientRect()
        const centerX = getTouchCenter(e.touches) - rect.left

        // Calculate zoom delta based on pinch distance change
        const scaleFactor = newDistance / lastTouchDistance.current
        const delta = scaleFactor > 1 ? -1 : 1 // Negative delta = zoom in

        if (Math.abs(scaleFactor - 1) > 0.01) {
          zoomAtPoint(delta * 50, centerX) // 50 is arbitrary sensitivity
          lastTouchDistance.current = newDistance
        }
      }
    }

    const handleTouchEnd = () => {
      isDragging.current = false
      lastTouchDistance.current = null
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)
    container.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [enabled, containerRef, pan, zoomAtPoint])
}

export default useTimelineZoom
